import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { SelectData, TaskPanelData } from "../../../../models";
import { searchOrganisationUsers } from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";

interface UseTaskPanelRightProps {
  currentTask: TaskPanelData;
  setCurrentTask: Dispatch<SetStateAction<TaskPanelData>>;
}

interface UseTaskPanelRightReturnData {
  currentOrganisationUuid: string | undefined;
  assigneeList: SelectData;
  handleAssigneeSearch: (search: string) => void;
  handleSelectAssignee: (value: string) => void;
}
export const useTaskPanelRight = ({
  currentTask,
  setCurrentTask,
}: UseTaskPanelRightProps): UseTaskPanelRightReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const [assigneeList, setAssigneeList] = useState<SelectData>([]);

  const handleAssigneeSearch = async (search: string): Promise<void> => {
    if (!currentOrganisationUuid) return;

    const response = await searchOrganisationUsers({
      organisationUuid: currentOrganisationUuid,
      paging: { limit: 50, beforeCursor: "", afterCursor: "" },
      sort: [{ key: "user.fullName" as "results.user.fullName", direction: "asc" }],
      filter: {
        results: {
          active: { operator: "eq", value: true },
          user: { fullName: { operator: "startsWith", value: search } },
        },
      },
    });

    if (response.status === Status.Success && response.data?.results) {
      const list = response.data.results.flatMap((el) =>
        el.user.uuid && el.user.fullName ? { key: el.user.uuid, value: el.user.fullName } : []
      );
      // Only real users are listed — clearing an assignee is done with the Clear button.
      setAssigneeList(list);
    }
  };

  const handleSelectAssignee = (value: string): void => {
    setCurrentTask((prevTask) => {
      /*
        The Autocomplete reports an empty key on every keystroke, not only when its text is cleared, so
        this is someone typing to find a replacement rather than a deliberate unassign. Leave the task
        alone: resetting here would drop the status and reminder of an ordinary reassignment. Unassigning
        is the explicit Clear action, and submitting without an assignee drops the reminder anyway.
      */
      if (!value) {
        return prevTask;
      }

      // Assigning a task moves it to "To do" unless the user already picked a real status — a
      // new task starts with no status at all, and an unassigned one carries "Unassigned".
      const hasChosenStatus =
        prevTask.status !== null && prevTask.status !== "" && prevTask.status !== TaskConstants.TASK_STATUS_UNASSIGNED;

      return {
        ...prevTask,
        assignedToUserUuid: value,
        assignedToUserName: assigneeList.find((user) => user.key === value)?.value?.toString() ?? null,
        status: hasChosenStatus ? prevTask.status : TaskConstants.TASK_STATUS_TO_DO,
      };
    });
  };

  useEffect(() => {
    if (currentTask.assignedToUserName) handleAssigneeSearch(currentTask.assignedToUserName);
    handleAssigneeSearch(currentTask.assignedToUserName ?? "");
  }, [currentTask.assignedToUserName]);

  return {
    currentOrganisationUuid,
    assigneeList,
    handleAssigneeSearch,
    handleSelectAssignee,
  };
};
